import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import CommonThead, { ContextMenu, ColumnVisibilityDialog } from "./CommonThead";
import CommonTBody from "./CommonTBody";
import EmptyState from "./components/EmptyState";
import LoadingState from "./components/LoadingState";
import fields from "./fields";
import utilities from "./UtilityFunctions";
import ExportDialog from "./components/ExportDialog";
import useColumnPrefs from "./useColumnPrefs";
import useServerSearch from "./hooks/useServerSearch";
import { useIsMobile } from "./hooks/useMediaQuery";
import { MiniBar, NotStartedPill, progColor, paceColor, lastPmtColor, MUTED, VISUAL_SHEETS } from "./cellVisuals";
import { DEAL_SHEETS } from "./defaultSheetLayout";
import { isColumnVisible, activeFilters as listActiveFilters } from "./columnVisibility";
import SheetFilterChips from "./components/SheetFilterChips";
import { DropdownMenu } from "@radix-ui/themes";
import {
    Search, ArrowUpDown, ListFilter, Columns3, Download, X,
    ChevronLeft, ChevronRight, ArrowUp, ArrowDown, AlertTriangle,
} from "lucide-react";

const CAR_SHEETS = ["Inventory", "Committed", "Cash", "Credit", "CreditCleared"];
// Deal sheets (opened via the AddEditCommitted modal) get a fixed, meaningful
// mobile-card layout — who / which plate / how much — instead of whatever the
// leading column order happens to be. Single source shared with useFieldOrder.
const IMG_BASE_URL = "https://app.autodealerug.com";

/**
 * One data view per sheet, two presentations (MOBILE_PLAN.md Phase 2):
 * - desktop/tablet ≥768px: the classic table (CommonThead + CommonTBody),
 *   exactly as each sheet rendered it before;
 * - mobile <768px: toolbar (search / sort / filter / columns / export) +
 *   tappable card list + compact pager for server-paginated sheets.
 *
 * `pagination`/`setPagination` are optional: when present the search box
 * drives the server search (like the desktop summary bar), otherwise it's a
 * live client-side quick filter over the visible fields of the loaded rows.
 */
export default function CommonDataView(props) {
    const isMobile = useIsMobile();
    return isMobile ? <MobileSheetView {...props} /> : <DesktopTable {...props} />;
}

function DesktopTable({
    objectsArray, sortData, sortOrder, fieldOrder, setFieldOrder,
    parentSheetName, filterData, filterObject, sheetName, isLoading = false,
    selectRow, selectedRow, openModalEdit, primaryKeyName,
    formattingRules, rowFormattingRule, selection,
}) {
    // The keys the header checkbox can act on: this page only, since that is
    // all that is loaded.
    const pageIds = useMemo(
        () => (selection ? objectsArray.map((r) => r[primaryKeyName]) : []),
        [selection, objectsArray, primaryKeyName]
    );
    return (
        <table className="sheet-table w-full">
            <CommonThead
                objectsArray={objectsArray}
                sortObjectsArray={sortData}
                sortOrder={sortOrder}
                fieldOrder={fieldOrder}
                setFieldOrder={setFieldOrder}
                parentSheetName={parentSheetName}
                filterData={filterData}
                filterObject={filterObject}
                sheetName={sheetName}
                selection={selection}
                pageIds={pageIds}
            />
            <CommonTBody
                isLoading={isLoading}
                objectsArray={objectsArray}
                filterObject={filterObject}
                selectRow={selectRow}
                selectedRow={selectedRow}
                fieldOrder={fieldOrder}
                openModalEdit={openModalEdit}
                primaryKeyName={primaryKeyName}
                formattingRules={formattingRules}
                rowFormattingRule={rowFormattingRule}
                sheetName={sheetName}
                selection={selection}
            />
        </table>
    );
}

const formatValue = (row, fieldName) =>
    utilities.formatIntoInput(row[fieldName], fields[fieldName]?.dataType);

//Human "time ago" for a payment date, e.g. "Last week", "2 months ago".
const relativeTime = (value) => {
    if (!value) return "Never";
    const then = new Date(value);
    if (isNaN(then.getTime())) return "—";
    const days = Math.floor((Date.now() - then.getTime()) / 86400000);
    if (days < 0) return "—";
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (weeks === 1) return "Last week";
    if (days < 30) return `${weeks} weeks ago`;
    const months = Math.floor(days / 30);
    if (months === 1) return "Last month";
    if (months < 12) return `${months} months ago`;
    const years = Math.floor(days / 365);
    return years === 1 ? "Last year" : `${years} years ago`;
};

//Aging colours shared by the cards (roughly 30-day months): green when fresh,
//orange in the middle band, red (with a warning icon) once stale. The style
//helpers return null when the value isn't usable so the field renders plain.
const AGING_COLORS = { green: "#16a34a", orange: "#ea580c", red: "#dc2626" };
const agingStyle = (color) => ({ color, fontWeight: 600 });

//Days-in-stock: fresh <7mo green, 7–<10mo orange, ≥10mo red.
const stockAgeStyle = (days) => {
    const d = Number(days);
    if (!Number.isFinite(d)) return null;
    if (d < 210) return agingStyle(AGING_COLORS.green);
    if (d < 300) return agingStyle(AGING_COLORS.orange);
    return agingStyle(AGING_COLORS.red);
};

//Last-paid recency: thresholds from cellVisuals.lastPmtColor (35d green, 65d orange, older red).
//Special cases preserved: never-paid stays hard red; a caller may short-circuit
//before calling this when balance is cleared (shows green "N/A" instead).
const lastPaidStyle = (value) => {
    if (!value) return agingStyle(AGING_COLORS.red); //never paid — flag it
    const then = new Date(value);
    if (isNaN(then.getTime())) return null;
    const days = Math.floor((Date.now() - then.getTime()) / 86400000);
    if (days < 0) return null;
    // Delegate to cellVisuals' color ramp so desktop dot and mobile text stay in sync.
    const color = lastPmtColor(days);
    return agingStyle(color);
};

function MobileSheetView({
    objectsArray, sortData, sortOrder, fieldOrder, setFieldOrder,
    parentSheetName, filterData, filterObject, sheetName,
    openModalEdit, primaryKeyName, formattingRules, rowFormattingRule,
    selectedRow, pagination, setPagination, searchPlaceholder = "Search…", selection,
    isLoading = false,
}) {
    const serverSearch = !!(pagination && setPagination);
    //Same live-search hook the desktop SheetFooter uses. On an unpaginated
    //sheet it degrades to local state and the filter below stays client-side.
    const { searchTerm, setSearchTerm, submitNow, clearSearch } =
        useServerSearch(pagination, setPagination);
    const [filterField, setFilterField] = useState(null);
    const [showColumnsDialog, setShowColumnsDialog] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const { persistFieldOrder, applyVisibilityUpdate } = useColumnPrefs(parentSheetName, setFieldOrder);

    //Shared with the desktop header and body, and it force-shows a column that
    //carries an active filter — see columnVisibility.js.
    const visibleFields = useMemo(
        () => fieldOrder.filter((f) => isColumnVisible(f, filterObject)),
        [fieldOrder, filterObject]
    );

    //openModalEdit takes an index into objectsArray, so the quick filter must
    //carry original indices along instead of producing a re-indexed subset.
    const indexedRows = useMemo(() => {
        const indexed = objectsArray.map((row, idx) => ({ row, idx }));
        const term = searchTerm.trim().toLowerCase();
        if (serverSearch || !term) return indexed;
        return indexed.filter(({ row }) =>
            visibleFields.some((f) =>
                String(formatValue(row, f.name)).toLowerCase().includes(term)
            )
        );
    }, [objectsArray, searchTerm, serverSearch, visibleFields]);

    const goToPage = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            setPagination((prev) => ({ ...prev, currentPage: page }));
        }
    };

    const hideField = (name) => {
        setFieldOrder((prev) => {
            const newArr = prev.map((f) => (f.name === name ? { ...f, hidden: true } : f));
            persistFieldOrder(newArr);
            return newArr;
        });
        setFilterField(null);
    };

    //Only for the toolbar's "has filters" dot; the chips themselves are the
    //shared component below.
    const activeFilters = listActiveFilters(filterObject);

    return (
        <>
            <div className="msheet-toolbar">
                <div className="msheet-search">
                    <Search size={16} className="msheet-search-icon" aria-hidden="true" />
                    <input
                        type="search"
                        enterKeyHint="search"
                        className="msheet-search-input"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={serverSearch ? (e) => { if (e.key === "Enter") submitNow(); } : undefined}
                    />
                    {searchTerm !== "" && (
                        <button className="msheet-search-clear" onClick={clearSearch} aria-label="Clear search">
                            <X size={16} />
                        </button>
                    )}
                </div>

                {/* Sort: toggles asc/desc on the picked field (same as the
                    desktop context menu's sort) */}
                <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                        <button className="msheet-tool-btn" aria-label="Sort">
                            <ArrowUpDown size={18} />
                        </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                        {visibleFields.map((f) => (
                            <DropdownMenu.Item key={f.name} onClick={() => sortData(f.name)}>
                                {fields[f.name].displayName}
                                {sortOrder?.[f.name] === "asc" && <ArrowUp size={14} />}
                                {sortOrder?.[f.name] === "desc" && <ArrowDown size={14} />}
                            </DropdownMenu.Item>
                        ))}
                    </DropdownMenu.Content>
                </DropdownMenu.Root>

                {/* Filter: pick a field, then the same filter menu the desktop
                    right-click opens. onCloseAutoFocus is prevented because
                    the menu's close-focus-return would land "outside" the
                    just-mounted filter popover and dismiss it immediately. */}
                <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                        <button
                            className={`msheet-tool-btn${activeFilters.length ? " has-filters" : ""}`}
                            aria-label="Filter"
                        >
                            <ListFilter size={18} />
                        </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
                        {visibleFields.map((f) => (
                            <DropdownMenu.Item
                                key={f.name}
                                onClick={() => setTimeout(() => setFilterField(f.name), 50)}
                            >
                                {fields[f.name].displayName}
                                {filterObject?.[f.name]?.filterType && " 🔍"}
                            </DropdownMenu.Item>
                        ))}
                    </DropdownMenu.Content>
                </DropdownMenu.Root>

                <button className="msheet-tool-btn" aria-label="Columns" onClick={() => setShowColumnsDialog(true)}>
                    <Columns3 size={18} />
                </button>
                <button
                    className="msheet-tool-btn"
                    aria-label="Download Excel"
                    onClick={() => setShowExportDialog(true)}
                >
                    <Download size={18} />
                </button>
            </div>

            {/* Extracted to components/SheetFilterChips.jsx on 2026-09-13 so the
                desktop sheet could have the same control, which it had never
                had. This is the same markup in the same place; nothing about
                the mobile view changed. */}
            <SheetFilterChips filterObject={filterObject} filterData={filterData} />

            <CardList
                isLoading={isLoading}
                indexedRows={indexedRows}
                visibleFields={visibleFields}
                sheetName={sheetName}
                openModalEdit={openModalEdit}
                primaryKeyName={primaryKeyName}
                formattingRules={formattingRules}
                rowFormattingRule={rowFormattingRule}
                selectedRow={selectedRow}
                selection={selection}
            />

            {serverSearch && pagination.totalPages > 1 && (
                <div className="msheet-pager">
                    <button
                        className="msheet-tool-btn"
                        disabled={!pagination.hasPrevPage}
                        onClick={() => goToPage(pagination.currentPage - 1)}
                        aria-label="Previous page"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <span className="msheet-pager-info">
                        Page {pagination.currentPage} / {Math.max(1, pagination.totalPages)}
                    </span>
                    <button
                        className="msheet-tool-btn"
                        disabled={!pagination.hasNextPage}
                        onClick={() => goToPage(pagination.currentPage + 1)}
                        aria-label="Next page"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            )}

            <ExportDialog
                open={showExportDialog}
                onOpenChange={setShowExportDialog}
                pageRows={indexedRows.map(({ row }) => row)}
                fieldOrder={fieldOrder}
                sheetName={sheetName}
                pagination={pagination}
                filterObject={filterObject}
            />

            {filterField && (
                <ContextMenu
                    fieldName={filterField}
                    fieldType={fields[filterField]?.dataType}
                    position={{ x: 16, y: 130 }}
                    onHide={() => setFilterField(null)}
                    onFilter={filterData}
                    filterObject={filterObject}
                    onSort={() => { sortData(filterField); setFilterField(null); }}
                    sortOrder={sortOrder}
                    onHideColumn={() => hideField(filterField)}
                    onToggleColumnVisibilityDialog={() => { setFilterField(null); setShowColumnsDialog(true); }}
                />
            )}

            {showColumnsDialog && ReactDOM.createPortal(
                <ColumnVisibilityDialog
                    fieldOrder={fieldOrder}
                    fields={fields}
                    onClose={() => setShowColumnsDialog(false)}
                    onUpdateVisibility={applyVisibilityUpdate}
                />,
                document.body
            )}
        </>
    );
}

function CardList({
    indexedRows, visibleFields, sheetName, openModalEdit, primaryKeyName,
    formattingRules, rowFormattingRule, selectedRow, selection, isLoading = false,
}) {
    const showImage = CAR_SHEETS.includes(sheetName);
    //Deal sheets show a fixed, meaningful layout — the details a user wants
    //first: the customer as the card title, then the vehicle (make + model) on
    //its own line above the plate + balance. Everything else keeps the generic
    //behaviour: the first two visible fields form the title and the next four
    //are label/value pairs, driven by the user's saved column order.
    const firstRow = indexedRows[0]?.row;
    const isDealSheet = DEAL_SHEETS.includes(sheetName) && firstRow;
    const isInventory = sheetName === "Inventory" && firstRow;
    const hasField = (name) => fields[name] && name in firstRow;

    //Per-row card content: { title, details:[{ key, label, value, full, styleName, valueStyle, warn }] }.
    const buildCard = (row) => {
        if (isInventory) {
            //Vehicle first: "2007 Toyota Wish" as the title, then the details a
            //user scans for — body shape, plate, colour, and how long it has sat
            //in stock (aging-coloured, with a warning icon once it's stale).
            const title = ["ModelYear", "Make", "Model"]
                .filter(hasField).map((n) => formatValue(row, n)).filter(Boolean).join(" ") || "—";
            const details = [];
            for (const name of ["Shape", "NumberPlate", "Color"]) {
                if (hasField(name)) details.push({ key: name, label: fields[name].displayName, value: formatValue(row, name), styleName: name });
            }
            if (hasField("Days")) {
                const valueStyle = stockAgeStyle(row.Days);
                details.push({
                    key: "Days",
                    label: fields.Days.displayName,
                    value: formatValue(row, "Days"),
                    valueStyle,
                    warn: valueStyle?.color === AGING_COLORS.red,
                });
            }
            return { title, details };
        }
        if (isDealSheet) {
            const vehicle = ["Make", "Model"].filter(hasField).map((n) => formatValue(row, n)).filter(Boolean).join(" ");
            const details = [];
            if (vehicle) details.push({ key: "__vehicle", label: "Vehicle", value: vehicle });
            //Once the balance is cleared, payment recency is moot — show "N/A"
            //in green rather than an ever-reddening stale date.
            const balance = hasField("TotalBalance") ? Number(row.TotalBalance) : NaN;
            const paidOff = Number.isFinite(balance) && balance <= 0;
            if (paidOff) {
                details.push({ key: "__lastpaid", label: "Last Paid", value: "N/A", valueStyle: agingStyle(AGING_COLORS.green) });
            } else {
                const lpStyle = lastPaidStyle(row.LastPmtDate);
                details.push({
                    key: "__lastpaid", label: "Last Paid", value: relativeTime(row.LastPmtDate),
                    valueStyle: lpStyle, warn: lpStyle?.color === AGING_COLORS.red,
                });
            }
            for (const name of ["NumberPlate", "TotalBalance"]) {
                if (!hasField(name)) continue;
                const detail = { key: name, label: fields[name].displayName, value: formatValue(row, name), styleName: name };
                //Draining bar under the balance: only on Credit/CreditCleared (VISUAL_SHEETS).
                //Cash/Cancelled rows have TotalBalance too but the visual context differs.
                if (name === "TotalBalance" && VISUAL_SHEETS.has(sheetName)) {
                    const credit = Number(row.TotalCredit);
                    const bal = Number(row.TotalBalance);
                    if (Number.isFinite(credit) && credit > 0 && Number.isFinite(bal)) {
                        const fill = Math.max(0, Math.min(100, (bal / credit) * 100));
                        detail.below = <MiniBar pct={fill} color={progColor(100 - fill)} />;
                    }
                }
                details.push(detail);
            }
            //Listing completeness, Inventory only, and only when something is
            //actually missing. A card reading "100%" under every finished
            //vehicle is a line of noise on the smallest screen in the product;
            //the reason this is on the card at all is to mark the ones a buyer
            //cannot filter for.
            if (sheetName === "Inventory" && hasField("ListingCompleteness")) {
                const pct = Number(row.ListingCompleteness);
                if (Number.isFinite(pct) && pct < 100) {
                    details.push({
                        key: "__listing",
                        label: fields.ListingCompleteness.displayName,
                        value: `${Math.round(pct)}%`,
                        below: <MiniBar pct={pct} color={progColor(pct)} />,
                        warn: pct < 67,
                    });
                }
            }
            //Performance score, only when the user keeps that column visible AND on credit sheets.
            if (hasField("PaymentPerformanceScore") && VISUAL_SHEETS.has(sheetName)) {
                const score = row.PaymentPerformanceScore;
                //Normalized records store NULL numerics as "" — treat as blank.
                const blank = score === null || score === undefined || score === "";
                const detail = { key: "__score", label: fields.PaymentPerformanceScore.displayName };
                if (blank) {
                    //On CreditCleared, a blank score means the deal ended before scoring —
                    //show a muted dash, not "Not started" (which implies it could still start).
                    detail.node = sheetName === "CreditCleared"
                        ? <span style={{ color: MUTED }}>—</span>
                        : <NotStartedPill />;
                } else {
                    //A BAR, NOT THE DIAL THE TABLE DRAWS. The dial is 40px square
                    //and the figure lives inside the ring, so it makes its cell
                    //nearly twice as tall as every other one on the card — 76px
                    //against 47 — and a four-card screen pays that four times.
                    //The bar is the same reading in one line plus 6px: the number
                    //goes where every other value on the card goes, tinted on the
                    //same ramp, and the fill carries the comparison.
                    //Same two conventions as Total Balance and Listing
                    //Completeness directly above, which were already bars here.
                    //The score can exceed 100 — MiniBar clamps the FILL and the
                    //label keeps the true figure, exactly as the dial did.
                    const pct = Number(score);
                    detail.value = `${Math.round(pct)}%`;
                    detail.valueStyle = { color: paceColor(pct) };
                    detail.below = <MiniBar pct={pct} color={paceColor(pct)} />;
                }
                details.push(detail);
            }
            //The deal queries return the customer as FirstName/LastName (individuals)
            //or CompanyName (companies) — there is no single "CustomerName" column.
            const person = ["FirstName", "LastName"].filter(hasField).map((n) => formatValue(row, n)).filter(Boolean).join(" ").trim();
            const company = hasField("CompanyName") ? formatValue(row, "CompanyName") : "";
            const title = person || company || vehicle || "—";
            return { title, details };
        }
        const title = visibleFields.slice(0, 2).map((f) => formatValue(row, f.name)).filter(Boolean).join(" ") || "—";
        const details = visibleFields.slice(2, 6).map((f) => ({
            key: f.name, label: fields[f.name].displayName, value: formatValue(row, f.name), styleName: f.name,
        }));
        return { title, details };
    };

    //A LAST DETAIL WITH NOTHING BESIDE IT TAKES BOTH COLUMNS.
    //
    //The fields are a two-column grid, so an odd count leaves the final cell
    //paired with empty space. On the credit sheets that cell is the payment
    //performance score, and it was the worst case of it: "PERFORMANCE SCORE (%)"
    //is 137px of label in a 132px column, so it wrapped to two lines and made
    //the card ~13px taller — while half the row sat unused right next to it.
    //
    //Conditional on the count being ODD rather than hard-coded on the score,
    //because which details exist depends on the sheet and on what the row
    //carries: `hasField` skips a column the query did not return, and Inventory
    //adds a listing-completeness row only when something is missing. Spanning
    //unconditionally would push the score onto a row of its own in exactly the
    //configurations where it currently has a neighbour — the opposite of the
    //point. `.msheet-card-field--full` already existed for this and had never
    //had a caller.
    const cardContent = (row) => {
        const card = buildCard(row);
        const n = card.details.length;
        if (n % 2 === 1) {
            card.details = card.details.map((d, i) => (i === n - 1 ? { ...d, full: true } : d));
        }
        return card;
    };

    //Deep links (notifications) pass the selected primary key — bring it into view.
    const selectedRef = useRef(null);
    useEffect(() => {
        selectedRef.current?.scrollIntoView({ block: "center" });
    }, [selectedRow, indexedRows.length]);

    if (indexedRows.length === 0) {
        // Loading is NOT empty. Before this, a fetch in flight rendered the
        // empty state, so the sheet said "No data available" and then filled in.
        return isLoading ? <LoadingState /> : <EmptyState />;
    }

    return (
        <div className="msheet-cards">
            {indexedRows.map(({ row, idx }) => {
                const isSelected = selectedRow !== "" && selectedRow != null && row[primaryKeyName] == selectedRow;
                const { title, details } = cardContent(row);
                return (
                    <div
                        key={row[primaryKeyName] ?? idx}
                        ref={isSelected ? selectedRef : undefined}
                        className={`msheet-card${isSelected ? " is-selected" : ""}`}
                        style={rowFormattingRule ? rowFormattingRule(row) : undefined}
                        onClick={() => openModalEdit(idx)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter") openModalEdit(idx); }}
                    >
                        {/* Sits before the thumbnail so the tick column lines up
                            down the list whether or not a card has an image.
                            stopPropagation because the whole card opens the modal. */}
                        {selection && (
                            <label
                                className="msheet-card-check"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <input
                                    type="checkbox"
                                    aria-label={`Select ${title}`}
                                    checked={selection.ids.has(row[primaryKeyName])}
                                    onChange={() => selection.toggle(row[primaryKeyName])}
                                />
                            </label>
                        )}
                        {showImage && <CardThumb primaryImage={row.PrimaryImage} />}
                        <div className="msheet-card-body">
                            <div className="msheet-card-title">
                                {title}
                            </div>
                            <div className="msheet-card-fields">
                                {details.map((d) => (
                                    <div key={d.key} className={`msheet-card-field${d.full ? " msheet-card-field--full" : ""}`}>
                                        {/* `title` because the label is
                                            line-clamped: two lines is enough
                                            for everything in the app today, but
                                            a longer displayName should still be
                                            readable rather than silently cut. */}
                                        <span className="msheet-card-label" title={d.label}>{d.label}</span>
                                        <span
                                            className="msheet-card-value"
                                            style={{
                                                ...(d.node == null ? (d.valueStyle ?? (d.styleName ? formattingRules?.[d.styleName]?.formatByValue(row[d.styleName]) : undefined)) : undefined),
                                                ...(d.warn ? { display: "inline-flex", alignItems: "center", gap: 3 } : null),
                                            }}
                                        >
                                            {d.node != null ? d.node : (<>{d.warn && <AlertTriangle size={13} aria-hidden="true" style={{ flexShrink: 0 }} />}{d.value}</>)}
                                        </span>
                                        {d.below != null && d.below}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <ChevronRight size={18} className="msheet-card-chevron" aria-hidden="true" />
                    </div>
                );
            })}
        </div>
    );
}

function CardThumb({ primaryImage }) {
    if (!primaryImage || typeof primaryImage !== "string") {
        return (
            <div className="msheet-card-thumb msheet-card-thumb-empty">
                <svg className="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            </div>
        );
    }
    return (
        <div className="msheet-card-thumb">
            <img
                src={`${IMG_BASE_URL}${primaryImage.replace(/\.jpg$/i, "_64.jpg")}`}
                alt=""
                loading="lazy"
                onError={(e) => {
                    const src = e.target.src;
                    if (src.includes("_64.jpg")) {
                        e.target.src = `${IMG_BASE_URL}${primaryImage}`;
                    } else {
                        e.target.onerror = null;
                        e.target.src = `${IMG_BASE_URL}/images/cars/placeholder.png`;
                    }
                }}
            />
        </div>
    );
}
